import { RecordManagementLabel } from '@aibees/elephant/feature/search-drawer/components/record-management-labels/components/record-management-label';
import { Label } from '@aibees/elephant/types/label';
import { IconName } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Stack, Typography } from '@mui/material';
import { memo, useCallback, useEffect, useState } from 'react';
import { RecordManagementAddButton } from '../../../record-management-add-button';
import { LabelManagementProvider } from '../../../search-table-action-dialog-lists/context';
import { useLabelAction } from '../../hooks';
import { useCurrentLabels } from '../../hooks/use-current-labels';
import { useLabelEntityConfig } from '../../hooks/use-label-entity-config';
import { useSearchType } from '../../hooks/use-search-type';
import { LabelActionDialog } from '../label-action-dialog';
import * as SX from './sx';
import type { LabelActionCellProps as Props } from './types';

const NOOP = () => {};

const LabelActionCellDialog = memo(
  ({
    type,
    currentLabelIds,
    id,
    searchTypeOverride,
    onLabelsSelect,
    selectedLabels,
    open,
    onClose,
  }: Props & { open: boolean; onClose: () => void }) => {
    const labelAction = useLabelAction({
      type,
      currentLabelIds,
      id,
      searchTypeOverride,
      onLabelsSelect,
      selectedLabels,
    });

    useEffect(() => {
      if (open) {
        labelAction.handleOpenDialog();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
      if (!labelAction.isDialogOpen && !open) {
        onClose();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [labelAction.isDialogOpen]);

    return <LabelActionDialog labelAction={labelAction} type={type} />;
  }
);
LabelActionCellDialog.displayName = 'LabelActionCellDialog';

interface LabelActionCellInnerProps extends Props {
  currentLabels: Label[];
  isLabel: boolean;
  icon: IconName;
}

const LabelActionCellInner = memo(
  ({
    className,
    type,
    currentLabelIds,
    id,
    searchTypeOverride,
    onLabelsSelect,
    selectedLabels,
    disabled,
    currentLabels,
    isLabel,
    icon,
  }: LabelActionCellInnerProps) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [hasOpenedDialog, setHasOpenedDialog] = useState(false);

    const handleStopPropagation = useCallback(
      (e: React.MouseEvent) => e.stopPropagation(),
      []
    );

    const handleOpenDialog = useCallback(() => {
      setDialogOpen(true);
      setHasOpenedDialog(true);
    }, []);

    const handleCloseDialog = useCallback(() => {
      setDialogOpen(false);
    }, []);

    return (
      <LabelManagementProvider onChangeLabels={NOOP}>
        <Stack
          className={classnames('LabelActionCell', className)}
          sx={SX.root}
          onClick={handleStopPropagation}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            sx={SX.container}
          >
            <Stack direction="row" alignItems="center" gap={0.5} flex={1}>
              {currentLabels.length === 0 ? (
                <Typography variant="body3" color="grey.400">
                  No {isLabel ? 'labels' : 'lists'}
                </Typography>
              ) : (
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                  flexWrap="wrap"
                >
                  {currentLabels[0] && (
                    <RecordManagementLabel
                      key={currentLabels[0].id}
                      label={currentLabels[0]}
                      id={id}
                    />
                  )}
                </Stack>
              )}
            </Stack>
            <RecordManagementAddButton
              disabled={disabled}
              onClick={handleOpenDialog}
              value={currentLabels?.length}
              icon={icon}
            />
          </Stack>
        </Stack>
        {hasOpenedDialog && (
          <LabelActionCellDialog
            type={type}
            currentLabelIds={currentLabelIds}
            id={id}
            searchTypeOverride={searchTypeOverride}
            onLabelsSelect={onLabelsSelect}
            selectedLabels={selectedLabels}
            disabled={disabled}
            open={dialogOpen}
            onClose={handleCloseDialog}
          />
        )}
      </LabelManagementProvider>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.className === nextProps.className &&
      prevProps.type === nextProps.type &&
      prevProps.id === nextProps.id &&
      prevProps.disabled === nextProps.disabled &&
      prevProps.currentLabels === nextProps.currentLabels &&
      prevProps.isLabel === nextProps.isLabel &&
      prevProps.icon === nextProps.icon &&
      prevProps.onLabelsSelect === nextProps.onLabelsSelect &&
      prevProps.selectedLabels === nextProps.selectedLabels
    );
  }
);
LabelActionCellInner.displayName = 'LabelActionCellInner';

/**
 * Thin wrapper that subscribes to bootstrap-labels query and resolves
 * currentLabels / isLabel / icon. The inner cell is memo'd so it only
 * re-renders when the resolved values actually change.
 */
export const LabelActionCell = memo(
  (props: Props) => {
    const { type, currentLabelIds, searchTypeOverride, selectedLabels } = props;

    const searchType = useSearchType(searchTypeOverride);
    const { isLabel, icon } = useLabelEntityConfig({ type, searchType });
    const currentLabels = useCurrentLabels({
      type,
      currentLabelIds,
      propsSelectedLabels: selectedLabels,
    });

    return (
      <LabelActionCellInner
        {...props}
        currentLabels={currentLabels}
        isLabel={isLabel}
        icon={icon}
      />
    );
  },
  (prevProps, nextProps) => {
    const prevIds = prevProps.currentLabelIds?.slice().sort().join(',') ?? '';
    const nextIds = nextProps.currentLabelIds?.slice().sort().join(',') ?? '';
    return (
      prevProps.className === nextProps.className &&
      prevProps.type === nextProps.type &&
      prevProps.id === nextProps.id &&
      prevProps.disabled === nextProps.disabled &&
      prevProps.searchTypeOverride === nextProps.searchTypeOverride &&
      prevProps.onLabelsSelect === nextProps.onLabelsSelect &&
      prevProps.selectedLabels === nextProps.selectedLabels &&
      prevIds === nextIds
    );
  }
);
LabelActionCell.displayName = 'LabelActionCell';
